import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MapPin, Loader2, Crosshair, Calendar, Store as StoreIcon, X } from "lucide-react";
import { toast } from "sonner";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StoreCard from "@/components/StoreCard";
import EventCard from "@/components/EventCard";
import MapView from "@/components/MapView";
import RsvpDialog from "@/components/RsvpDialog";

import { fetchStores, fetchNearby, fetchEvents, seedData, fetchVisitCounts, recordVisit } from "@/lib/api";

const LocatorPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const tab = searchParams.get("tab") || "stores";

  const [stores, setStores] = useState([]);
  const [events, setEvents] = useState([]);
  const [visitCounts, setVisitCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [activeStoreId, setActiveStoreId] = useState(null);
  const [rsvpEvent, setRsvpEvent] = useState(null);
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [nearbyActive, setNearbyActive] = useState(false);

  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "stores") next.delete("tab");
    else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    (async () => {
      try {
        let allStores = await fetchStores();
        if (allStores.length === 0) {
          await seedData();
          allStores = await fetchStores();
        }
        setStores(allStores);
        const [allEvents, counts] = await Promise.all([fetchEvents(), fetchVisitCounts()]);
        setEvents(allEvents);
        // If no visit data yet (older DB), call seed to backfill
        if (Object.keys(counts).length === 0) {
          await seedData();
          const c2 = await fetchVisitCounts();
          setVisitCounts(c2);
        } else {
          setVisitCounts(counts);
        }
      } catch (e) {
        console.error(e);
        toast.error("Could not load stores. Please refresh.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Record a visit once per session per store when user clicks a card
  const handleStoreClick = (id) => {
    setActiveStoreId(id);
    try {
      const key = `zl_visited_${id}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        recordVisit(id);
        setVisitCounts((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
      }
    } catch {
      // ignore storage errors
    }
  };

  const locateMe = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setUserLocation({ lat, lng });
        try {
          const nearby = await fetchNearby(lat, lng, 50);
          if (nearby.length === 0) {
            toast.info("No Zebralearn stores within 50 km. Showing all stores.");
            setNearbyActive(false);
          } else {
            setStores(nearby);
            setNearbyActive(true);
            toast.success(`Found ${nearby.length} store${nearby.length > 1 ? "s" : ""} within 50 km`);
          }
        } catch {
          toast.error("Could not load nearby stores");
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        if (err.code === 1) toast.error("Location permission denied. Please enable location.");
        else toast.error("Could not get your location");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const clearNearby = async () => {
    setNearbyActive(false);
    setUserLocation(null);
    setActiveStoreId(null);
    const all = await fetchStores();
    setStores(all);
  };

  const filteredStores = useMemo(() => {
    if (!query.trim()) return stores;
    const q = query.toLowerCase();
    return stores.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.city.toLowerCase().includes(q) ||
        s.address.toLowerCase().includes(q)
    );
  }, [stores, query]);

  const visibleEvents = useMemo(() => {
    if (nearbyActive) {
      const nearbyIds = new Set(stores.map((s) => s.id));
      return events.filter((e) => nearbyIds.has(e.store_id));
    }
    return events;
  }, [events, stores, nearbyActive]);

  const openRsvp = (event) => {
    setRsvpEvent(event);
    setRsvpOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Header onLocate={locateMe} locating={locating} totalStores={stores.length} />

      <main className="flex-1">
        {/* Page heading bar */}
        <section className="relative px-4 sm:px-6 lg:px-10 pt-8 md:pt-12 pb-4 border-b border-zinc-900 overflow-hidden">
          <div className="zl-blob -top-32 -right-32" />
          <div className="zl-blob -bottom-40 -left-32 opacity-60" />
          <div className="relative flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#FF5612] mb-2">
                Store Locator
              </div>
              <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
                Find Zebralearn books{" "}
                <span className="text-[#FF5612]">near you.</span>
              </h1>
              <p className="text-sm md:text-base text-zinc-400 mt-3 max-w-2xl leading-relaxed">
                {stores.length}+ stores across India · Live in-store events ·
                Search above or hit <span className="text-white font-medium">Near Me</span> for
                stores within 50 km.
              </p>
            </div>
            <button
              onClick={locateMe}
              disabled={locating}
              data-testid="hero-locate-btn"
              className="sm:hidden inline-flex items-center gap-2 bg-[#FF5612] hover:bg-[#FF6B2B] text-white rounded-full px-5 py-3 font-semibold text-sm transition-colors disabled:opacity-60"
            >
              {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
              Near Me
            </button>
          </div>
        </section>

        {/* Split layout */}
        <div className="px-4 sm:px-6 lg:px-10 py-6 md:py-10 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-6 lg:gap-10">
          {/* LEFT — Tabs + List */}
          <div className="min-w-0">
            {/* Tabs */}
            <div
              className="relative inline-flex p-1 rounded-full bg-zinc-900 border border-zinc-800 mb-6"
              data-testid="locator-tabs"
            >
              <button
                onClick={() => setTab("stores")}
                data-testid="tab-stores"
                className={`relative z-10 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                  tab === "stores"
                    ? "bg-[#FF5612] text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <StoreIcon className="w-4 h-4" />
                Stores
                <span className={tab === "stores" ? "text-white/80" : "text-zinc-500"}>
                  ({filteredStores.length})
                </span>
              </button>
              <button
                onClick={() => setTab("events")}
                data-testid="tab-events"
                className={`relative z-10 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                  tab === "events"
                    ? "bg-[#FF5612] text-white"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <Calendar className="w-4 h-4" />
                Events
                <span className={tab === "events" ? "text-white/80" : "text-zinc-500"}>
                  ({visibleEvents.length})
                </span>
              </button>
            </div>

            {/* Nearby banner */}
            {nearbyActive && (
              <div
                data-testid="nearby-banner"
                className="mb-5 flex items-center justify-between gap-3 bg-[#FF5612]/10 border border-[#FF5612]/40 rounded-2xl px-4 py-3"
              >
                <div className="flex items-center gap-2.5">
                  <MapPin className="w-4 h-4 text-[#FF5612]" />
                  <span className="text-sm text-white">
                    Showing stores within 50 km of your location
                  </span>
                </div>
                <button
                  onClick={clearNearby}
                  data-testid="clear-nearby-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-300 hover:text-white"
                >
                  <X className="w-3.5 h-3.5" /> Show All
                </button>
              </div>
            )}

            {/* CONTENT */}
            {loading ? (
              <div className="py-16 text-center text-zinc-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
                Loading stores...
              </div>
            ) : tab === "stores" ? (
              <>
                {filteredStores.length === 0 ? (
                  <div
                    data-testid="no-stores-empty"
                    className="rounded-2xl border border-dashed border-zinc-800 p-10 text-center"
                  >
                    <MapPin className="w-8 h-8 mx-auto mb-3 text-zinc-500" />
                    <div className="font-display font-bold text-lg text-white">No stores found</div>
                    <p className="text-sm text-zinc-400 mt-1">
                      Try a different search or clear filters.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="stores-list">
                    {filteredStores.map((s, i) => (
                      <StoreCard
                        key={s.id}
                        store={s}
                        active={s.id === activeStoreId}
                        onClick={handleStoreClick}
                        index={i}
                        visitsLast30Days={visitCounts[s.id]}
                      />
                    ))}
                  </div>
                )}

                {/* Events teaser below stores list */}
                {visibleEvents.length > 0 && (
                  <div id="events" className="mt-12 pt-10 border-t border-zinc-900">
                    <div className="flex items-end justify-between mb-6 gap-4">
                      <div>
                        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#FF5612] mb-2">
                          In-Store Happenings
                        </div>
                        <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-white">
                          Upcoming Events
                        </h2>
                      </div>
                      <button
                        onClick={() => setTab("events")}
                        data-testid="view-all-events"
                        className="text-xs font-semibold text-[#FF5612] hover:text-[#FF6B2B]"
                      >
                        View All →
                      </button>
                    </div>
                    <div className="space-y-4">
                      {visibleEvents.slice(0, 3).map((ev, i) => (
                        <EventCard key={ev.id} event={ev} onRsvp={openRsvp} index={i} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {visibleEvents.length === 0 ? (
                  <div
                    data-testid="no-events-empty"
                    className="rounded-2xl border border-dashed border-zinc-800 p-10 text-center"
                  >
                    <Calendar className="w-8 h-8 mx-auto mb-3 text-zinc-500" />
                    <div className="font-display font-bold text-lg text-white">No upcoming events</div>
                    <p className="text-sm text-zinc-400 mt-1">
                      Check back soon for in-store readings and launches.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="events-list">
                    {visibleEvents.map((ev, i) => (
                      <EventCard key={ev.id} event={ev} onRsvp={openRsvp} index={i} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* RIGHT — Map */}
          <aside className="lg:sticky lg:top-28 self-start" data-testid="map-aside">
            <div className="rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-950 zl-card-shadow">
              <div className="border-b border-zinc-900 px-4 py-3 flex items-center justify-between bg-zinc-950">
                <div>
                  <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500">
                    Live Map
                  </div>
                  <div className="font-display font-bold text-sm text-white">
                    {filteredStores.length} location{filteredStores.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <button
                  onClick={locateMe}
                  disabled={locating}
                  data-testid="map-locate-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#FF5612] hover:bg-[#FF6B2B] text-white px-3 py-1.5 text-[11px] font-semibold transition-colors disabled:opacity-60"
                >
                  {locating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Crosshair className="w-3 h-3" />}
                  Locate
                </button>
              </div>
              <div className="h-[420px] md:h-[540px] lg:h-[640px]">
                <MapView
                  stores={filteredStores}
                  userLocation={userLocation}
                  activeStoreId={activeStoreId}
                  onMarkerClick={handleStoreClick}
                />
              </div>
            </div>
          </aside>
        </div>
      </main>

      <Footer />

      <RsvpDialog event={rsvpEvent} open={rsvpOpen} onOpenChange={setRsvpOpen} />
    </div>
  );
};

export default LocatorPage;
